import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { Control, FieldPath, UseFormReturn } from 'react-hook-form';
import { useController, useForm, useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

import type { ContractFormValues } from '~/components/contracts/types';
import { ContractForm } from '~/components/contracts/types';
import { LAISelect, LAITextField } from '~/components/form';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';

type KpiTarget = ContractForm['propertyContract']['cars'][number];

type CarKpiTableProps = {
  value: KpiTarget;
  index: number;
  control: Control<ContractFormValues>;
};

const maintenanceUnitOptions = [
  { label: 'Visit/s', value: 'visits' },
  { label: 'Hour/s', value: 'hours' },
];

// Template form schema for "Apply to All" functionality
const templateKpiSchema = z.object({
  regularCallbackResponseTime: z.coerce.number().min(1),
  emergencyCallbackResponseTime: z.coerce.number().min(1),
  regularCallbackRate: z.coerce.number().min(0),
  emergencyCallbackRate: z.coerce.number().min(0),
  maintenanceQuantity: z.coerce.number(),
  maintenanceUnit: z.enum(['visits', 'hours']),
  maintenanceTimePeriod: z.coerce.number().min(0),
});

type TemplateKpiForm = z.infer<typeof templateKpiSchema>;

type MaintenanceUnitSelectProps = {
  name: FieldPath<ContractFormValues>;
  control: Control<ContractFormValues>;
};

const MaintenanceUnitSelect = ({ name, control }: MaintenanceUnitSelectProps) => {
  const { field } = useController({ name, control });
  return (
    <LAISelect
      sx={{ minWidth: 120 }}
      label="Unit"
      name={name}
      hookFormRef={field.ref}
      value={field.value}
      onChange={field.onChange}
      onBlur={() => field.onBlur()}
      disabled={field.disabled}
      options={maintenanceUnitOptions}
    />
  );
};

type TemplateMaintenanceUnitSelectProps = {
  name: FieldPath<TemplateKpiForm>;
  control: Control<TemplateKpiForm>;
};

const TemplateMaintenanceUnitSelect = ({ name, control }: TemplateMaintenanceUnitSelectProps) => {
  const { field } = useController({ name, control });
  return (
    <LAISelect
      sx={{ minWidth: 120 }}
      label="Unit"
      name={name}
      hookFormRef={field.ref}
      value={field.value}
      onChange={field.onChange}
      onBlur={() => field.onBlur()}
      disabled={field.disabled}
      options={maintenanceUnitOptions}
    />
  );
};

const CarKpiTable = ({ value, index, control }: CarKpiTableProps) => {
  const terms = useLocaleTerms();
  const quantityInputProps = useMemo(() => {
    return {
      slotProps: {
        input: {
          inputProps: {
            min: value.maintenanceUnit === 'hours' ? 0.01 : 1,
            step: value.maintenanceUnit === 'hours' ? 0.01 : 1,
          },
        },
      },
    };
  }, [value.maintenanceUnit]);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ p: 1 }}>
      <Table size="medium">
        <TableHead
          sx={{
            backgroundColor: 'primary.light',
          }}
        >
          <TableRow
            sx={{
              '& th:first-child': {
                borderTopLeftRadius: 4,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              },
              '& th:last-child': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              },
            }}
          >
            <TableCell>
              <Typography variant="subtitle2">{terms.unit} Name</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2">License</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle2">Device Type</Typography>
            </TableCell>
          </TableRow>
          <TableRow
            sx={{
              '& th:first-child': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 4,
                borderBottomLeftRadius: 0,
                border: 'none',
              },
              '& th': {
                border: 'none',
              },
              '& th:last-child': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 4,
                borderBottomLeftRadius: 0,
              },
            }}
          >
            <TableCell>
              <Typography variant="body2">{value.name}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{value.ahjId ? value.ahjId : '-'}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{value.elevatorType}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.regularCallbackResponse}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Minutes"
                  size="small"
                  type="number"
                  name={`propertyContract.cars.${index}.regularCallbackResponseTime`}
                  control={control}
                  slotProps={{
                    htmlInput: {
                      step: 1,
                      min: 1,
                    },
                    input: {
                      sx: {
                        width: 80,
                      },
                    },
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <div>from the Call Time until Arrival Time</div>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.emergencyCallbackResponse}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Minutes"
                  size="small"
                  type="number"
                  name={`propertyContract.cars.${index}.emergencyCallbackResponseTime`}
                  control={control}
                  slotProps={{
                    htmlInput: {
                      step: 1,
                      min: 1,
                    },
                    input: {
                      sx: {
                        width: 80,
                      },
                    },
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <div>from the Call Time until Arrival Time</div>
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.callbackRateTarget}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Rate"
                  size="small"
                  type="number"
                  name={`propertyContract.cars.${index}.regularCallbackRate`}
                  control={control}
                  slotProps={{
                    input: {
                      sx: {
                        width: 80,
                      },
                    },
                    htmlInput: {
                      min: 0,
                    },
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.emergencyCallbackRateTarget}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Rate"
                  size="small"
                  type="number"
                  name={`propertyContract.cars.${index}.emergencyCallbackRate`}
                  control={control}
                  slotProps={{
                    input: {
                      sx: {
                        width: 80,
                      },
                    },
                    htmlInput: {
                      min: 0,
                    },
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">Maintenance Target</Typography>
            </TableCell>
            <TableCell colSpan={2} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LAITextField
                label="Quantity"
                size="small"
                type="number"
                name={`propertyContract.cars.${index}.maintenanceQuantity`}
                control={control}
                sx={{ width: 80 }}
                {...quantityInputProps}
              />
              <MaintenanceUnitSelect
                name={`propertyContract.cars.${index}.maintenanceUnit`}
                control={control}
              />
              <Typography variant="body2">every</Typography>
              <LAITextField
                label="Time Period"
                size="small"
                type="number"
                name={`propertyContract.cars.${index}.maintenanceTimePeriod`}
                control={control}
                sx={{ width: 80 }}
                slotProps={{
                  htmlInput: {
                    min: 0,
                  },
                }}
              />
              <Typography variant="body2">month/s</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TemplateKpiForm = ({
  templateForm,
  quantityInputProps,
}: {
  templateForm: UseFormReturn<TemplateKpiForm>;
  quantityInputProps: {
    slotProps: {
      input: {
        inputProps: {
          min: number;
          step: number;
        };
      };
    };
  };
}) => {
  const { control } = templateForm;
  const terms = useLocaleTerms();

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ p: 1 }}>
      <Table size="medium">
        <TableHead sx={{ backgroundColor: 'primary.light' }}>
          <TableRow
            sx={{
              '& th:first-child': {
                borderTopLeftRadius: 4,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              },
              '& th:last-child': {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 4,
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              },
            }}
          >
            <TableCell colSpan={3}>
              <Typography variant="subtitle2">Template (Apply to All Cars)</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.regularCallbackResponse}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Minutes"
                  size="small"
                  type="number"
                  name="regularCallbackResponseTime"
                  control={control}
                  slotProps={{
                    htmlInput: { step: 1, min: 1 },
                    input: { sx: { width: 80 } },
                    inputLabel: { shrink: true },
                  }}
                />
                <div>from the Call Time until Arrival Time</div>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.emergencyCallbackResponse}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Minutes"
                  size="small"
                  type="number"
                  name="emergencyCallbackResponseTime"
                  control={control}
                  slotProps={{
                    htmlInput: { step: 1, min: 1 },
                    input: { sx: { width: 80 } },
                    inputLabel: { shrink: true },
                  }}
                />
                <div>from the Call Time until Arrival Time</div>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.callbackRateTarget}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Rate"
                  size="small"
                  type="number"
                  name="regularCallbackRate"
                  control={control}
                  slotProps={{
                    input: { sx: { width: 80 } },
                    htmlInput: { min: 0 },
                    inputLabel: { shrink: true },
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">{terms.emergencyCallbackRateTarget}</Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <div>Max</div>
                <LAITextField
                  label="Rate"
                  size="small"
                  type="number"
                  name="emergencyCallbackRate"
                  control={control}
                  slotProps={{
                    input: { sx: { width: 80 } },
                    htmlInput: { min: 0 },
                    inputLabel: { shrink: true },
                  }}
                />
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <Typography variant="subtitle2">Maintenance Target</Typography>
            </TableCell>
            <TableCell colSpan={2} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <LAITextField
                label="Quantity"
                size="small"
                type="number"
                name="maintenanceQuantity"
                control={control}
                sx={{ width: 80 }}
                {...quantityInputProps}
              />
              <TemplateMaintenanceUnitSelect name="maintenanceUnit" control={control} />
              <Typography variant="body2">every</Typography>
              <LAITextField
                label="Time Period"
                size="small"
                type="number"
                name="maintenanceTimePeriod"
                control={control}
                sx={{ width: 80 }}
                slotProps={{
                  htmlInput: { min: 0 },
                }}
              />
              <Typography variant="body2">month/s</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const fieldsToSync = [
  'regularCallbackResponseTime',
  'emergencyCallbackResponseTime',
  'regularCallbackRate',
  'emergencyCallbackRate',
  'maintenanceQuantity',
  'maintenanceUnit',
  'maintenanceTimePeriod',
] as const;

export const ContractKpiTargetsSectionFields = () => {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const [applyToAllMode, setApplyToAllMode] = useState(false);

  const cars = useWatch({ control, name: 'propertyContract.cars' });

  const templateForm = useForm<TemplateKpiForm>({
    resolver: zodResolver(templateKpiSchema),
    mode: 'onChange',
  });

  const { control: templateControl, subscribe: templateSubscribe } = templateForm;
  const templateMaintenanceUnit = useWatch({
    control: templateControl,
    name: 'maintenanceUnit',
  });

  const quantityInputProps = useMemo(() => {
    return {
      slotProps: {
        input: {
          inputProps: {
            min: templateMaintenanceUnit === 'hours' ? 0.01 : 1,
            step: templateMaintenanceUnit === 'hours' ? 0.01 : 1,
          },
        },
      },
    };
  }, [templateMaintenanceUnit]);

  // Sync template values to all cars
  useEffect(() => {
    if (applyToAllMode) {
      return templateSubscribe({
        formState: { values: true },
        callback: ({ values: val }) => {
          cars.forEach((_, index) => {
            fieldsToSync.forEach((field) => {
              const value = val[field];
              if (value !== undefined) {
                setValue(`propertyContract.cars.${index}.${field}`, value, {
                  shouldValidate: true,
                });
              }
            });
          });
        },
      });
    }
  }, [templateSubscribe, cars, applyToAllMode, setValue]);

  // Handle mode toggle with proper initialization
  const handleModeToggle = (checked: boolean) => {
    if (checked && cars.length > 0) {
      // Initialize template with first car's values
      const firstCar = cars[0];

      templateForm.reset({
        regularCallbackResponseTime: firstCar.regularCallbackResponseTime,
        emergencyCallbackResponseTime: firstCar.emergencyCallbackResponseTime,
        regularCallbackRate: firstCar.regularCallbackRate,
        emergencyCallbackRate: firstCar.emergencyCallbackRate,
        maintenanceQuantity: firstCar.maintenanceQuantity,
        maintenanceUnit: firstCar.maintenanceUnit,
        maintenanceTimePeriod: firstCar.maintenanceTimePeriod,
      });
    }

    setApplyToAllMode(checked);
  };

  return (
    <Box maxWidth="md" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {cars.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={applyToAllMode}
                onChange={(e) => handleModeToggle(e.target.checked)}
              />
            }
            label="Apply to All Cars"
          />
          {applyToAllMode ? (
            <Typography variant="body2" color="text.secondary">
              Changes will be applied to all {cars.length} cars
            </Typography>
          ) : null}
        </Box>
      )}

      {cars.length > 0 ? (
        applyToAllMode ? (
          <TemplateKpiForm templateForm={templateForm} quantityInputProps={quantityInputProps} />
        ) : (
          cars.map((field, index) => {
            return <CarKpiTable key={field.id} value={field} index={index} control={control} />;
          })
        )
      ) : (
        <Typography variant="body2" textAlign="center">
          No cars selected for this contract
        </Typography>
      )}
    </Box>
  );
};
